import { useEffect, useId, useState } from "react";
import { ErrorNote, Loading } from "../../../components/Feedback";
import { loadProjectDatasets } from "../projectDatasets";

// "Datasets to look in" of a file column (ColumnEditor) or a file-name pattern (FilePatterns):
// the project's datasets as checkboxes, grouped by study, any number chosen. Nothing chosen
// means "this dataset" (file_scope / scope [] — the build looks in [own]), not "none"; the hint
// says so. An id in `value` that is no longer listed (the dataset was deleted) stays, ticked, as
// "dataset <id> (not found)" until it is unticked. The backend refuses ids outside the project
// (422, services/file_refs.py), so the picker needs no rule of its own.
//
//   props: projectId, currentId (the dataset being edited), value (dataset ids), onChange(ids)

const box = { width: 18, height: 18, margin: 0, accentColor: "var(--dp-accent-dark)" };
const row = { display: "flex", gap: 8, alignItems: "center", fontSize: 14, padding: "2px 0" };

export default function DatasetPicker({ projectId, currentId, value, onChange, label = "Datasets to look in" }) {
  const [datasets, setDatasets] = useState(null);
  const [error, setError] = useState(null);
  const hintId = useId();

  useEffect(() => {
    let cancelled = false;
    loadProjectDatasets(projectId).then(ds => { if (!cancelled) setDatasets(ds); },
                                        e => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [projectId]);

  const chosen = value || [];
  const toggle = id => onChange(chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id]);
  const studies = [];
  (datasets || []).forEach(d => {
    let s = studies.find(x => x.id === d.study_id && x.name === d.study_name);
    if (!s) { s = { id: d.study_id, name: d.study_name, datasets: [] }; studies.push(s); }
    s.datasets.push(d);
  });
  const missing = datasets ? chosen.filter(id => !datasets.some(d => d.id === id)) : [];

  return (
    <fieldset aria-describedby={hintId}
              style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 12px", margin: 0, minWidth: 0 }}>
      <legend style={{ fontSize: 14, fontWeight: 600, padding: "0 6px" }}>{label}</legend>
      <p id={hintId} style={{ fontSize: 12, color: "#4b5563", margin: "0 0 6px" }}>
        Nothing chosen: this dataset only. Choose this dataset too to look in it as well.
      </p>
      <ErrorNote error={error} />
      {!datasets && !error && <Loading label="Loading the project's datasets…" />}
      {studies.map(s => (
        <div key={`${s.id}-${s.name}`} role="group" aria-label={s.name} style={{ marginBottom: 4 }}>
          <div aria-hidden="true" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{s.name}</div>
          {s.datasets.map(d => (
            <label key={d.id} style={row}>
              <input type="checkbox" style={box} checked={chosen.includes(d.id)} onChange={() => toggle(d.id)} />
              <span>{d.name}{d.id === currentId ? " (this dataset)" : ""}</span>
            </label>
          ))}
        </div>
      ))}
      {missing.map(id => (
        <label key={id} style={row}>
          <input type="checkbox" style={box} checked onChange={() => toggle(id)} />
          <span>dataset {id} (not found)</span>
        </label>
      ))}
    </fieldset>
  );
}
